import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, CheckCircle2, UserX, ArrowRight, Clock } from "lucide-react";
import { Button } from "../../components/ui/button";
import { api, apiError } from "../../lib/api";

const TONE = {
  confirmed: "bg-teal-500/10 text-teal-300 border-teal-500/20",
  pending_payment: "bg-amber-500/10 text-amber-300 border-amber-500/20",
  completed: "bg-zinc-800 text-mb-fg2",
  cancelled: "bg-zinc-800 text-mb-fg2",
  no_show: "bg-red-500/10 text-red-300 border-red-500/20",
};

export default function Today() {
  const [today, setToday] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = () => {
    setLoading(true);
    Promise.all([
      api.get("/doctor/appointments"),
      api.get("/doctor/appointments/upcoming"),
    ]).then(([a, b]) => { setToday(a.data); setUpcoming(b.data); })
      .finally(() => setLoading(false));
  };
  useEffect(refresh, []);

  const act = async (id, kind) => {
    try {
      await api.post(`/doctor/appointments/${id}/${kind}`,
        kind === "complete" ? { notes: null } : {});
      toast.success(kind === "complete" ? "Marked completed" : "Marked no-show");
      refresh();
    } catch (e) { toast.error(apiError(e, "Failed")); }
  };

  const completedToday = today.filter((a) => a.status === "completed").length;
  const remainingToday = today.filter((a) => ["confirmed", "pending_payment"].includes(a.status)).length;
  const upcoming7 = upcoming.length;

  return (
    <div className="space-y-8" data-testid="doctor-today">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-teal-400">◖ today</div>
        <h1 className="mt-2 text-3xl sm:text-4xl tracking-tight font-light">
          {new Date().toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" })}
        </h1>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Stat testid="stat-today" label="Today" value={loading ? "—" : today.length} />
        <Stat testid="stat-completed" label="Completed" value={loading ? "—" : completedToday} />
        <Stat testid="stat-week" label="Next 7 days" value={loading ? "—" : upcoming7} />
      </div>

      <section className="rounded-xl border border-mb-border bg-mb-surface" data-testid="today-list">
        <div className="px-6 py-4 border-b border-mb-border flex items-center justify-between">
          <div>
            <div className="text-sm font-medium">Today's appointments</div>
            <div className="text-xs text-mb-fg2 mt-0.5">{remainingToday} remaining</div>
          </div>
          <Link to="/doctor/appointments" className="text-xs text-teal-400 hover:text-teal-300 inline-flex items-center gap-1">
            All appointments <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
        <ul className="divide-y divide-mb-border">
          {loading && <li className="px-6 py-8 text-sm text-mb-fg2 text-center">Loading…</li>}
          {!loading && today.length === 0 && (
            <li className="px-6 py-12 text-sm text-mb-fg2 text-center" data-testid="today-empty">
              No appointments today.
            </li>
          )}
          {today.map((a) => (
            <li key={a.id} data-testid={`today-row-${a.id}`}
              className="px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="text-sm tabular-nums w-20 inline-flex items-center gap-1.5 text-zinc-200">
                <Clock className="h-3 w-3 text-teal-400" />
                {new Date(a.slot_start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{a.patient?.full_name || "—"}</div>
                <div className="text-xs text-mb-fg2 truncate">{a.patient?.email}</div>
              </div>
              <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded border ${TONE[a.status] || ""}`}>{a.status.replace("_", " ")}</span>
              <div className="flex gap-2">
                <Link to={`/doctor/appointments/${a.id}`} data-testid={`today-view-${a.id}`}
                  className="text-xs text-teal-400 hover:text-teal-300 px-2 py-1.5">View</Link>
                {["confirmed", "pending_payment"].includes(a.status) && (
                  <>
                    <Button variant="ghost" onClick={() => act(a.id, "complete")} data-testid={`today-complete-${a.id}`}
                      className="hover:bg-teal-500/10 text-teal-300 h-8"><CheckCircle2 className="h-4 w-4" /></Button>
                    <Button variant="ghost" onClick={() => act(a.id, "no_show")} data-testid={`today-no-show-${a.id}`}
                      className="hover:bg-red-500/10 text-red-400 h-8"><UserX className="h-4 w-4" /></Button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="rounded-xl border border-mb-border bg-mb-surface" data-testid="upcoming-7-list">
        <div className="px-6 py-4 border-b border-mb-border flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-teal-400" />
          <div className="text-sm font-medium">Next 7 days</div>
        </div>
        <ul className="divide-y divide-mb-border">
          {upcoming.length === 0 && (
            <li className="px-6 py-8 text-sm text-mb-fg2 text-center">Nothing scheduled.</li>
          )}
          {upcoming.slice(0, 8).map((a) => (
            <li key={a.id} className="px-6 py-3 flex items-center gap-4 text-sm" data-testid={`upcoming-row-${a.id}`}>
              <div className="text-mb-fg2 tabular-nums w-32">{new Date(a.slot_start).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
              <div className="text-white flex-1 truncate">{a.patient?.full_name}</div>
              <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded border ${TONE[a.status] || ""}`}>{a.status.replace("_", " ")}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-mb-border bg-mb-surface p-5">
      <div className="text-[10px] uppercase tracking-[0.25em] text-mb-fg2">{label}</div>
      <div className="mt-2 text-3xl font-light text-white">{value}</div>
    </div>
  );
}
